import { ShieldCheck } from "lucide-react";
import type { PolicyData } from "../PolicyShell";

export const AML_POLICY: PolicyData = {
  slug: "aml-policy",
  title: "AML & BSA Compliance Policy",
  subtitle: "",
  effectiveDate: "January 2026",
  icon: ShieldCheck,
  documentType: "Compliance Policy",
  version: "1.0",
  intro:
    "Prepared in accordance with the requirements of the Financial Crimes Enforcement Network, the U.S. Department of the Treasury, the Bank Secrecy Act (BSA), USA PATRIOT Act, OFAC sanctions regulations, and applicable Anti-Money Laundering (AML) obligations applicable to Money Services Businesses (MSBs).",
  sections: [
    {
      id: "purpose",
      number: "1",
      title: "Purpose",
      blocks: [
        {
          type: "p",
          text: "This AML/BSA Compliance Policy establishes the framework, controls, procedures, governance standards, and risk management processes implemented by CashTap to prevent the platform from being used for:",
        },
        {
          type: "ul",
          items: [
            "Money laundering",
            "Terrorist financing",
            "Fraud",
            "Financial crime",
            "Sanctions evasion",
            "Cybercrime",
            "Identity theft",
            "Unauthorized financial activity",
            "Illicit digital asset activity",
            "Human trafficking",
            "Synthetic identity fraud",
          ],
        },
        { type: "p", text: "This policy applies to all:" },
        {
          type: "ul",
          items: [
            "Employees",
            "Contractors",
            "Officers",
            "Vendors",
            "Users",
            "Merchants",
            "API partners",
            "Service providers",
            "Third-party integrations",
          ],
        },
      ],
    },
    {
      id: "company-overview",
      number: "2",
      title: "Company Overview",
      blocks: [
        {
          type: "p",
          text: "CashTap is a financial technology platform.",
        },
        { type: "p", text: "CashTap enables users to:" },
        {
          type: "ul",
          items: [
            "Send and receive USD and stablecoins",
            "Access virtual bank account infrastructure through regulated partners",
            "Create payment links and invoices",
            "Conduct peer-to-peer transactions",
            "Receive payouts",
            "Utilize custodial wallet infrastructure",
            "Access collection and merchant tools",
          ],
        },
        {
          type: "p",
          text: "CashTap operates as a technology and financial infrastructure platform and relies on regulated third-party financial institutions, custodial providers, banking partners, and payment infrastructure providers for regulated financial services.",
        },
        { type: "p", text: "CashTap does NOT:" },
        {
          type: "ul",
          items: [
            "Operate or manage gambling or casino platforms",
            "Custody fiat customer deposits directly",
            "Offer anonymous financial accounts",
            "Operate mixing or tumbling services",
            "Facilitate darknet activity",
            "Support sanctioned jurisdictions",
            "Facilitate illegal financial activity",
            "Provide unlicensed financial services",
          ],
        },
      ],
    },
    {
      id: "regulatory-framework",
      number: "3",
      title: "Regulatory Framework",
      blocks: [
        {
          type: "p",
          text: "CashTap maintains a compliance framework aligned with:",
        },
        {
          type: "ul",
          items: [
            "Bank Secrecy Act (BSA)",
            "USA PATRIOT Act",
            "FinCEN MSB guidance",
            "OFAC sanctions regulations",
            "AML regulations applicable to financial technology platforms",
            "Customer Identification Program (CIP) obligations",
            "Suspicious Activity Reporting (SAR) requirements",
            "Recordkeeping obligations",
            "Applicable state regulatory considerations",
            "Digital asset compliance obligations where applicable",
          ],
        },
        { type: "p", text: "Applicable regulatory references include:" },
        {
          type: "ul",
          items: [
            "FinCEN Official Website",
            "OFAC Sanctions Programs",
            "FFIEC BSA/AML Manual",
          ],
        },
      ],
    },
    {
      id: "msb-statement",
      number: "4",
      title: "Money Services Business (MSB) Statement",
      blocks: [
        {
          type: "p",
          text: "CashTap operates through Aixona LLC and maintains compliance obligations applicable to Money Services Businesses (MSBs) under applicable United States federal regulations.",
        },
        {
          type: "p",
          text: "CashTap maintains a risk-based AML compliance framework designed to align with FinCEN guidance and applicable BSA obligations.",
        },
      ],
    },
    {
      id: "aml-program-components",
      number: "5",
      title: "AML Program Components",
      blocks: [
        {
          type: "p",
          text: "CashTap maintains a written AML compliance program containing:",
        },
      ],
    },
    {
      id: "internal-controls",
      number: "5.1",
      title: "Internal Controls",
      level: 2,
      blocks: [
        {
          type: "p",
          text: "Policies, procedures, systems, and controls designed to detect and prevent suspicious activity and financial crime.",
        },
      ],
    },
    {
      id: "aml-compliance-officer",
      number: "5.2",
      title: "AML Compliance Officer",
      level: 2,
      blocks: [
        {
          type: "p",
          text: "Designation of an AML Compliance Officer with sufficient authority and independence.",
        },
      ],
    },
    {
      id: "independent-testing",
      number: "5.3",
      title: "Independent Testing",
      level: 2,
      blocks: [
        {
          type: "p",
          text: "Periodic independent testing and review of the AML program.",
        },
      ],
    },
    {
      id: "employee-training",
      number: "5.4",
      title: "Employee Training",
      level: 2,
      blocks: [
        {
          type: "p",
          text: "Risk-based AML and sanctions training for relevant personnel.",
        },
      ],
    },
    {
      id: "risk-based-monitoring",
      number: "5.5",
      title: "Risk-Based Monitoring",
      level: 2,
      blocks: [
        {
          type: "p",
          text: "Continuous monitoring of customer activity using risk-based methodologies.",
        },
      ],
    },
    {
      id: "risk-appetite",
      number: "6",
      title: "Risk Appetite Statement",
      blocks: [
        {
          type: "p",
          text: "CashTap maintains a conservative risk appetite regarding financial crime exposure and reserves the right to reject, suspend, or terminate relationships involving elevated AML, sanctions, fraud, reputational, or regulatory risk.",
        },
      ],
    },
    {
      id: "aml-governance",
      number: "7",
      title: "AML Governance Structure",
      blocks: [],
    },
    {
      id: "aml-officer-responsibilities",
      number: "7.1",
      title: "AML Compliance Officer Responsibilities",
      level: 2,
      blocks: [
        { type: "p", text: "The AML Compliance Officer is responsible for:" },
        {
          type: "ul",
          items: [
            "AML program oversight",
            "Regulatory communications",
            "SAR investigations and filing decisions",
            "OFAC compliance oversight",
            "Escalation handling",
            "Internal reporting",
            "Compliance training oversight",
            "Risk assessment management",
            "Audit coordination",
            "Regulatory examination coordination",
          ],
        },
        { type: "p", text: "The AML Compliance Officer has authority to:" },
        {
          type: "ul",
          items: [
            "Freeze accounts",
            "Reject transactions",
            "Escalate suspicious activity",
            "Restrict users",
            "Terminate business relationships",
            "File SARs",
            "Coordinate with law enforcement where required",
          ],
        },
      ],
    },
    {
      id: "enterprise-risk-assessment",
      number: "8",
      title: "Enterprise-Wide Risk Assessment",
      blocks: [
        {
          type: "p",
          text: "CashTap performs periodic AML risk assessments considering:",
        },
      ],
    },
    {
      id: "customer-risk",
      number: "8.1",
      title: "Customer Risk",
      level: 2,
      blocks: [
        { type: "p", text: "Including:" },
        {
          type: "ul",
          items: [
            "High-volume users",
            "P2P users",
            "Merchants",
            "International users",
            "Digitally exposed users",
            "PEPs",
            "High-risk industries",
          ],
        },
      ],
    },
    {
      id: "product-risk",
      number: "8.2",
      title: "Product Risk",
      level: 2,
      blocks: [
        { type: "p", text: "Including:" },
        {
          type: "ul",
          items: [
            "Stablecoin transactions",
            "Wallet services",
            "Payment links",
            "Collection services",
            "P2P transfers",
            "Instant settlement functionality",
          ],
        },
      ],
    },
    {
      id: "geographic-risk",
      number: "8.3",
      title: "Geographic Risk",
      level: 2,
      blocks: [
        {
          type: "p",
          text: "Enhanced monitoring applies to jurisdictions associated with:",
        },
        {
          type: "ul",
          items: [
            "OFAC sanctions",
            "FATF concerns",
            "Elevated fraud activity",
            "Weak AML frameworks",
          ],
        },
      ],
    },
    {
      id: "transaction-risk",
      number: "8.4",
      title: "Transaction Risk",
      level: 2,
      blocks: [
        { type: "p", text: "Higher-risk activity indicators include:" },
        {
          type: "ul",
          items: [
            "Structuring",
            "Rapid movement of funds",
            "Velocity spikes",
            "Wallet hopping",
            "Layering behavior",
            "Multiple linked accounts",
            "Third-party funding",
            "Unusual transaction patterns",
          ],
        },
      ],
    },
    {
      id: "cip",
      number: "9",
      title: "Customer Identification Program (CIP)",
      blocks: [
        {
          type: "p",
          text: "CashTap maintains a Customer Identification Program designed to verify user identity prior to account activation.",
        },
      ],
    },
    {
      id: "cip-individual",
      number: "9.1",
      title: "Required Individual Information",
      level: 2,
      blocks: [
        { type: "p", text: "CashTap may collect:" },
        {
          type: "ul",
          items: [
            "Full legal name",
            "Date of birth",
            "Residential address",
            "Government-issued identification",
            "Email address",
            "Phone number",
          ],
        },
      ],
    },
    {
      id: "cip-business",
      number: "9.2",
      title: "Required Business Information",
      level: 2,
      blocks: [
        { type: "p", text: "CashTap may collect:" },
        {
          type: "ul",
          items: [
            "Legal entity name",
            "Registration documents",
            "EIN or tax identification number",
            "Beneficial ownership information",
            "Nature of business",
            "Authorized representatives",
          ],
        },
      ],
    },
    {
      id: "kyc",
      number: "10",
      title: "Know Your Customer (KYC)",
      blocks: [
        {
          type: "p",
          text: "CashTap applies risk-based KYC verification procedures.",
        },
        { type: "p", text: "Verification methods may include:" },
        {
          type: "ul",
          items: [
            "Document verification",
            "Selfie and liveness verification",
            "Address verification",
            "Device analysis",
            "IP analysis",
            "Email verification",
            "Phone verification",
            "Fraud screening",
            "Sanctions screening",
            "Behavioral analytics",
          ],
        },
        { type: "p", text: "Accounts failing verification may be:" },
        {
          type: "ul",
          items: [
            "Restricted",
            "Suspended",
            "Rejected",
            "Escalated for manual review",
          ],
        },
      ],
    },
    {
      id: "beneficial-ownership",
      number: "11",
      title: "Beneficial Ownership Verification",
      blocks: [
        {
          type: "p",
          text: "For business accounts, CashTap may collect beneficial ownership information for individuals:",
        },
        {
          type: "ul",
          items: [
            "Owning 25% or more of the entity",
            "Exercising significant control over the entity",
          ],
        },
        {
          type: "p",
          text: "Additional documentation may be required for complex ownership structures.",
        },
      ],
    },
    {
      id: "ofac",
      number: "12",
      title: "OFAC & Sanctions Compliance",
      blocks: [
        { type: "p", text: "CashTap prohibits access by:" },
        {
          type: "ul",
          items: [
            "OFAC-sanctioned persons",
            "Blocked entities",
            "Restricted organizations",
            "Terrorist organizations",
            "Sanctioned jurisdictions",
          ],
        },
        { type: "p", text: "Screening may include:" },
        {
          type: "ul",
          items: [
            "OFAC SDN screening",
            "PEP screening",
            "Adverse media screening",
            "Sanctions database screening",
          ],
        },
        {
          type: "p",
          text: "Potential sanctions matches are escalated immediately.",
        },
        { type: "p", text: "CashTap reserves the right to:" },
        {
          type: "ul",
          items: [
            "Freeze accounts",
            "Reject transactions",
            "Restrict services",
            "Report activity where legally required",
          ],
        },
      ],
    },
    {
      id: "geographic-restrictions",
      number: "13",
      title: "Geographic Restrictions",
      blocks: [
        {
          type: "p",
          text: "CashTap may prohibit or restrict access from jurisdictions associated with:",
        },
        {
          type: "ul",
          items: [
            "Sanctions programs",
            "Elevated financial crime risk",
            "Weak AML enforcement",
            "Regulatory restrictions",
          ],
        },
        {
          type: "p",
          text: "Use of VPNs, proxies, or location masking tools may result in account suspension.",
        },
      ],
    },
    {
      id: "cdd",
      number: "14",
      title: "Customer Due Diligence (CDD)",
      blocks: [
        {
          type: "p",
          text: "CashTap performs ongoing customer due diligence based on customer risk profiles.",
        },
        { type: "p", text: "CDD measures may include:" },
        {
          type: "ul",
          items: [
            "Transaction monitoring",
            "Source of funds reviews",
            "Activity analysis",
            "Account behavior reviews",
            "Relationship purpose reviews",
          ],
        },
      ],
    },
    {
      id: "edd",
      number: "15",
      title: "Enhanced Due Diligence (EDD)",
      blocks: [
        { type: "p", text: "EDD procedures may apply to:" },
        {
          type: "ul",
          items: [
            "High-volume merchants",
            "High-risk industries",
            "PEPs",
            "High-risk geographies",
            "Complex ownership structures",
            "Users with elevated transaction activity",
          ],
        },
        { type: "p", text: "EDD measures may include:" },
        {
          type: "ul",
          items: [
            "Source of wealth verification",
            "Corporate documentation review",
            "Enhanced wallet analysis",
            "Bank statement reviews",
            "Manual compliance review",
            "Additional identity verification",
          ],
        },
      ],
    },
    {
      id: "stablecoin-compliance",
      number: "16",
      title: "Stablecoin & Digital Asset Compliance",
      blocks: [
        {
          type: "p",
          text: "CashTap maintains risk-based controls related to digital assets and stablecoin activity.",
        },
        { type: "p", text: "Controls may include:" },
        {
          type: "ul",
          items: [
            "Wallet screening",
            "Blockchain tracing",
            "Transaction monitoring",
            "Exposure analysis",
            "High-risk wallet identification",
            "Digital asset sanctions screening",
            "Travel Rule procedures where applicable",
          ],
        },
        {
          type: "p",
          text: "CashTap may utilize third-party blockchain analytics providers and custodial infrastructure partners for enhanced monitoring and compliance support.",
        },
      ],
    },
    {
      id: "transaction-monitoring",
      number: "17",
      title: "Transaction Monitoring",
      blocks: [
        {
          type: "p",
          text: "CashTap maintains automated and manual monitoring systems designed to identify suspicious activity.",
        },
        { type: "p", text: "Monitoring scenarios include:" },
        {
          type: "ul",
          items: [
            "Structuring behavior",
            "Rapid in/out activity",
            "Unusual transaction spikes",
            "Linked account behavior",
            "Shared device usage",
            "High-risk wallet exposure",
            "Velocity anomalies",
            "Unusual payment activity",
            "Synthetic identity indicators",
          ],
        },
      ],
    },
    {
      id: "ai-monitoring",
      number: "18",
      title: "AI & Automated Monitoring Systems",
      blocks: [
        {
          type: "p",
          text: "CashTap may utilize automated compliance technologies including:",
        },
        {
          type: "ul",
          items: [
            "Behavioral analytics",
            "Velocity detection",
            "Device intelligence",
            "Automated transaction scoring",
            "Pattern recognition systems",
            "Fraud risk engines",
            "Automated alert generation",
          ],
        },
      ],
    },
    {
      id: "blockchain-monitoring",
      number: "19",
      title: "Blockchain Monitoring",
      blocks: [
        {
          type: "p",
          text: "Where digital assets are involved, CashTap may utilize blockchain analytics tools to identify exposure to:",
        },
        {
          type: "ul",
          items: [
            "Sanctioned wallets",
            "Fraudulent wallets",
            "Stolen assets",
            "Ransomware exposure",
            "High-risk wallet activity",
            "Financial crime indicators",
          ],
        },
        {
          type: "p",
          text: "Transactions may be restricted based on blockchain risk scoring.",
        },
      ],
    },
    {
      id: "sar",
      number: "20",
      title: "Suspicious Activity Reporting (SAR)",
      blocks: [
        {
          type: "p",
          text: "CashTap investigates suspicious activity and files Suspicious Activity Reports where legally required.",
        },
        { type: "p", text: "Potential SAR triggers include:" },
        {
          type: "ul",
          items: [
            "Fraud",
            "Structuring",
            "Synthetic identities",
            "Identity theft",
            "Suspicious wallet activity",
            "Sanctions evasion",
            "High-risk behavioral anomalies",
            "Rapid movement of funds",
            "Suspicious account linkages",
          ],
        },
        { type: "p", text: "SAR confidentiality is strictly maintained." },
        {
          type: "p",
          text: "Users are never informed if a SAR has been filed.",
        },
      ],
    },
    {
      id: "ctr",
      number: "21",
      title: "Currency Transaction Reporting (CTR)",
      blocks: [
        {
          type: "p",
          text: "Where applicable, CashTap and its regulated partners comply with Currency Transaction Reporting requirements.",
        },
      ],
    },
    {
      id: "recordkeeping",
      number: "22",
      title: "Recordkeeping",
      blocks: [
        { type: "p", text: "CashTap maintains records related to:" },
        {
          type: "ul",
          items: [
            "KYC documentation",
            "Transactions",
            "Wallet activity",
            "Compliance investigations",
            "SAR reviews",
            "User communications",
            "Audit logs",
          ],
        },
        {
          type: "p",
          text: "Records are retained in accordance with legal and regulatory requirements.",
        },
      ],
    },
    {
      id: "travel-rule",
      number: "23",
      title: "Travel Rule Compliance",
      blocks: [
        {
          type: "p",
          text: "Where applicable, CashTap and its regulated partners may collect and transmit originator and beneficiary information for qualifying digital asset transfers.",
        },
      ],
    },
    {
      id: "fraud-prevention",
      number: "24",
      title: "Fraud Prevention Controls",
      blocks: [
        {
          type: "p",
          text: "CashTap maintains layered fraud prevention systems including:",
        },
        {
          type: "ul",
          items: [
            "Device fingerprinting",
            "Velocity checks",
            "IP reputation analysis",
            "Behavioral analytics",
            "Wallet screening",
            "Geolocation analysis",
            "Risk scoring systems",
            "Chargeback monitoring",
            "Session analysis",
          ],
        },
      ],
    },
    {
      id: "operational-controls",
      number: "25",
      title: "Operational Controls",
      blocks: [
        {
          type: "p",
          text: "CashTap may implement operational controls including:",
        },
        {
          type: "ul",
          items: [
            "Withdrawal cooldown periods",
            "Velocity limits",
            "Risk-based transaction holds",
            "Transaction thresholds",
            "Manual review queues",
            "Automated restrictions",
            "Account security reviews",
          ],
        },
      ],
    },
    {
      id: "cybersecurity",
      number: "26",
      title: "Cybersecurity & Account Protection",
      blocks: [
        {
          type: "p",
          text: "CashTap maintains cybersecurity controls designed to protect user accounts and platform infrastructure.",
        },
        { type: "p", text: "Controls may include:" },
        {
          type: "ul",
          items: [
            "Multi-factor authentication (MFA)",
            "Session monitoring",
            "API abuse prevention",
            "Suspicious login detection",
            "Account takeover monitoring",
            "Encryption",
            "Incident response procedures",
          ],
        },
      ],
    },
    {
      id: "prohibited-activities",
      number: "27",
      title: "Prohibited Activities",
      blocks: [
        { type: "p", text: "Users are prohibited from using CashTap for:" },
        {
          type: "ul",
          items: [
            "Money laundering",
            "Terrorist financing",
            "Fraud",
            "Scam activity",
            "Ponzi schemes",
            "Unauthorized money transmission",
            "Identity theft",
            "Synthetic identity fraud",
            "Cybercrime",
            "Sanctions evasion",
            "Sale of illegal goods",
            "Counterfeit activity",
            "Human trafficking",
            "Child exploitation",
          ],
        },
        { type: "p", text: "Violation may result in:" },
        {
          type: "ul",
          items: [
            "Account suspension",
            "Account termination",
            "Reporting to authorities",
            "Permanent platform restrictions",
          ],
        },
      ],
    },
    {
      id: "zero-tolerance",
      number: "28",
      title: "Zero-Tolerance Activities",
      blocks: [
        { type: "p", text: "CashTap maintains zero tolerance for:" },
        {
          type: "ul",
          items: [
            "Terrorist financing",
            "Human trafficking",
            "Child exploitation",
            "Sanctions evasion",
            "Synthetic identity abuse",
            "Stolen financial credentials",
            "Organized financial crime",
          ],
        },
      ],
    },
    {
      id: "high-risk-merchants",
      number: "29",
      title: "High-Risk Merchant Categories",
      blocks: [
        {
          type: "p",
          text: "CashTap may prohibit or restrict businesses involving:",
        },
        {
          type: "ul",
          items: [
            "Adult content",
            "Illegal pharmaceuticals",
            "Counterfeit products",
            "Weapons sales",
            "Pyramid schemes",
            "Anonymous financial services",
            "High-risk fraud-prone industries",
            "Unlicensed financial operations",
          ],
        },
        {
          type: "p",
          text: "Enhanced Due Diligence may apply to elevated-risk merchants.",
        },
      ],
    },
    {
      id: "merchant-monitoring",
      number: "30",
      title: "Merchant Monitoring Program",
      blocks: [
        {
          type: "p",
          text: "CashTap may conduct ongoing merchant monitoring including:",
        },
        {
          type: "ul",
          items: [
            "Transaction pattern analysis",
            "Excessive dispute monitoring",
            "Fraud rate reviews",
            "Payment activity analysis",
            "Velocity monitoring",
            "Operational risk reviews",
          ],
        },
      ],
    },
    {
      id: "p2p-controls",
      number: "31",
      title: "Peer-to-Peer (P2P) Risk Controls",
      blocks: [
        {
          type: "p",
          text: "CashTap applies additional controls to peer-to-peer activity including:",
        },
        {
          type: "ul",
          items: [
            "Transfer limits",
            "Velocity monitoring",
            "Recipient risk scoring",
            "Wallet analysis",
            "Device linkage analysis",
            "Behavioral monitoring",
            "Fraud scoring",
          ],
        },
      ],
    },
    {
      id: "api-compliance",
      number: "32",
      title: "API & Developer Compliance",
      blocks: [
        {
          type: "p",
          text: "CashTap may maintain controls related to APIs and developer integrations including:",
        },
        {
          type: "ul",
          items: [
            "API abuse monitoring",
            "Merchant API due diligence",
            "Fraudulent automation prevention",
            "Rate limiting controls",
            "Developer activity monitoring",
            "Security monitoring",
          ],
        },
      ],
    },
    {
      id: "vendor-oversight",
      number: "33",
      title: "Vendor & Third-Party Oversight",
      blocks: [
        { type: "p", text: "CashTap conducts due diligence on:" },
        {
          type: "ul",
          items: [
            "Banking partners",
            "Wallet providers",
            "Payment processors",
            "API providers",
            "Compliance vendors",
            "Infrastructure providers",
          ],
        },
        {
          type: "p",
          text: "Third parties are expected to maintain appropriate compliance standards.",
        },
      ],
    },
    {
      id: "partner-reliance",
      number: "34",
      title: "Partner Reliance Framework",
      blocks: [
        {
          type: "p",
          text: "Certain regulated financial services may be conducted through licensed banking institutions, custodial providers, payment processors, and compliance vendors.",
        },
        {
          type: "p",
          text: "CashTap performs due diligence and ongoing oversight of critical partners where appropriate.",
        },
      ],
    },
    {
      id: "case-management",
      number: "35",
      title: "Case Management & Investigation Procedures",
      blocks: [
        { type: "p", text: "CashTap maintains procedures for:" },
        {
          type: "ul",
          items: [
            "Compliance investigations",
            "Alert escalation",
            "Internal case management",
            "Evidence preservation",
            "Decision logging",
            "Escalation review",
            "SAR review workflows",
          ],
        },
      ],
    },
    {
      id: "law-enforcement",
      number: "36",
      title: "Law Enforcement Response Procedures",
      blocks: [
        { type: "p", text: "CashTap maintains procedures for handling:" },
        {
          type: "ul",
          items: [
            "Subpoenas",
            "Preservation requests",
            "Regulatory inquiries",
            "Emergency disclosure requests",
            "Law enforcement requests",
          ],
        },
      ],
    },
    {
      id: "training",
      number: "37",
      title: "Employee Training",
      blocks: [
        {
          type: "p",
          text: "Relevant employees receive AML and compliance training covering:",
        },
        {
          type: "ul",
          items: [
            "AML obligations",
            "OFAC sanctions",
            "Fraud indicators",
            "SAR escalation procedures",
            "Customer risk indicators",
            "Data protection obligations",
            "Regulatory reporting procedures",
          ],
        },
        {
          type: "p",
          text: "Training is documented and periodically refreshed.",
        },
      ],
    },
    {
      id: "employee-screening",
      number: "38",
      title: "Employee Screening & Hiring Controls",
      blocks: [
        {
          type: "p",
          text: "CashTap may implement employee screening procedures including:",
        },
        {
          type: "ul",
          items: [
            "Background checks",
            "Confidentiality agreements",
            "Compliance screening for sensitive roles",
            "Access control reviews",
          ],
        },
      ],
    },
    {
      id: "audit",
      number: "39",
      title: "Independent Audit & Testing",
      blocks: [
        {
          type: "p",
          text: "CashTap performs periodic independent testing of the AML program to assess:",
        },
        {
          type: "ul",
          items: [
            "Control effectiveness",
            "Monitoring adequacy",
            "Escalation procedures",
            "Sanctions controls",
            "Regulatory compliance",
          ],
        },
        { type: "p", text: "Audit findings are documented and remediated." },
      ],
    },
    {
      id: "data-privacy",
      number: "40",
      title: "Data Privacy & Retention",
      blocks: [
        { type: "p", text: "CashTap maintains procedures for:" },
        {
          type: "ul",
          items: [
            "Secure data handling",
            "Privacy protection",
            "Access restrictions",
            "Retention schedules",
            "Secure disposal of records",
            "Regulatory retention obligations",
          ],
        },
      ],
    },
    {
      id: "data-security",
      number: "41",
      title: "Data Security & Confidentiality",
      blocks: [
        {
          type: "p",
          text: "CashTap protects sensitive compliance information through:",
        },
        {
          type: "ul",
          items: [
            "Encryption",
            "Access controls",
            "Audit logging",
            "Secure storage",
            "Incident response procedures",
            "Least-privilege access controls",
          ],
        },
      ],
    },
    {
      id: "business-continuity",
      number: "42",
      title: "Business Continuity & Disaster Recovery",
      blocks: [
        {
          type: "p",
          text: "CashTap maintains business continuity and disaster recovery procedures designed to support operational resilience and incident recovery.",
        },
      ],
    },
    {
      id: "regulatory-cooperation",
      number: "43",
      title: "Regulatory Cooperation",
      blocks: [
        {
          type: "p",
          text: "CashTap cooperates with lawful requests from:",
        },
        {
          type: "ul",
          items: [
            "FinCEN",
            "OFAC",
            "Regulatory authorities",
            "Law enforcement agencies",
            "Courts and subpoenas",
          ],
        },
        {
          type: "p",
          text: "Information sharing occurs where legally required.",
        },
      ],
    },
    {
      id: "examination-readiness",
      number: "44",
      title: "Regulatory Examination Readiness",
      blocks: [
        { type: "p", text: "CashTap maintains procedures to support:" },
        {
          type: "ul",
          items: [
            "Regulatory examinations",
            "Audit readiness",
            "Documentation availability",
            "Compliance reporting",
            "Regulator cooperation",
          ],
        },
      ],
    },
    {
      id: "enforcement-actions",
      number: "45",
      title: "Enforcement Actions",
      blocks: [
        { type: "p", text: "CashTap reserves the right to:" },
        {
          type: "ul",
          items: [
            "Freeze accounts",
            "Restrict services",
            "Reject transactions",
            "Hold funds",
            "Suspend users",
            "Terminate accounts",
            "Escalate suspicious activity",
          ],
        },
      ],
    },
    {
      id: "escalation",
      number: "46",
      title: "Escalation Procedures",
      blocks: [
        { type: "p", text: "Employees must escalate:" },
        {
          type: "ul",
          items: [
            "Suspicious activity",
            "Sanctions concerns",
            "Fraud indicators",
            "Data breaches",
            "Regulatory requests",
            "Law enforcement inquiries",
          ],
        },
        { type: "p", text: "All escalations must be documented." },
      ],
    },
    {
      id: "red-flags",
      number: "47",
      title: "AML Red Flags",
      blocks: [
        { type: "p", text: "Examples of AML red flags include:" },
        {
          type: "ul",
          items: [
            "Multiple accounts using the same device",
            "Sudden transaction spikes",
            "Rapid withdrawals after deposits",
            "Frequent wallet changes",
            "Structuring behavior",
            "Third-party funding",
            "VPN or proxy usage",
            "Shared IP activity",
            "Excessive failed verification attempts",
          ],
        },
      ],
    },
    {
      id: "source-of-funds",
      number: "48",
      title: "Source of Funds Verification",
      blocks: [
        { type: "p", text: "CashTap may request documentation including:" },
        {
          type: "ul",
          items: [
            "Bank statements",
            "Pay stubs",
            "Invoices",
            "Tax documents",
            "Business contracts",
            "Digital asset transaction evidence",
          ],
        },
        {
          type: "p",
          text: "Failure to provide requested documentation may result in account restrictions.",
        },
      ],
    },
    {
      id: "transaction-limits",
      number: "49",
      title: "Transaction Limits & Threshold Controls",
      blocks: [
        { type: "p", text: "CashTap may maintain:" },
        {
          type: "ul",
          items: [
            "Daily transaction limits",
            "Monthly transaction limits",
            "Risk-based thresholds",
            "Enhanced review thresholds",
            "Withdrawal restrictions",
          ],
        },
      ],
    },
    {
      id: "compliance-tech",
      number: "50",
      title: "Compliance Systems & Technology Stack",
      blocks: [
        { type: "p", text: "CashTap may utilize:" },
        {
          type: "ul",
          items: [
            "Identity verification systems",
            "Blockchain analytics tools",
            "AML transaction monitoring systems",
            "Fraud prevention systems",
            "Device intelligence systems",
            "Case management systems",
            "Sanctions screening systems",
          ],
        },
      ],
    },
    {
      id: "regulatory-reporting",
      number: "51",
      title: "Regulatory Reporting",
      blocks: [
        {
          type: "p",
          text: "CashTap and its partners maintain procedures for:",
        },
        {
          type: "ul",
          items: [
            "SAR filing",
            "CTR reporting",
            "Law enforcement requests",
            "Regulatory examinations",
            "Compliance investigations",
          ],
        },
      ],
    },
    {
      id: "consumer-protection",
      number: "52",
      title: "Consumer Protection",
      blocks: [
        {
          type: "p",
          text: "CashTap maintains safeguards to protect users against:",
        },
        {
          type: "ul",
          items: [
            "Fraud",
            "Account takeover",
            "Unauthorized access",
            "Scam activity",
            "Identity theft",
          ],
        },
        {
          type: "p",
          text: "Users are encouraged to report suspicious activity immediately.",
        },
      ],
    },
    {
      id: "consumer-risk-disclosure",
      number: "53",
      title: "Consumer Risk Disclosure",
      blocks: [
        { type: "p", text: "Users acknowledge that:" },
        {
          type: "ul",
          items: [
            "Digital asset transactions may be irreversible",
            "Third-party financial partners may support certain services",
            "Fraudulent activity may result in account restrictions",
            "Stablecoin and blockchain transactions may involve operational risks",
          ],
        },
      ],
    },
    {
      id: "review-committee",
      number: "54",
      title: "Compliance Review Committee",
      blocks: [
        {
          type: "p",
          text: "CashTap may maintain periodic compliance review meetings to discuss:",
        },
        {
          type: "ul",
          items: [
            "Risk exposure",
            "Suspicious activity trends",
            "Escalation reviews",
            "Policy updates",
            "Regulatory developments",
          ],
        },
      ],
    },
    {
      id: "policy-certification",
      number: "55",
      title: "Periodic Policy Certification",
      blocks: [
        {
          type: "p",
          text: "Relevant employees and contractors may be required to periodically acknowledge and certify compliance with this AML/BSA Policy.",
        },
      ],
    },
    {
      id: "program-review",
      number: "56",
      title: "AML Program Review",
      blocks: [
        { type: "p", text: "This AML/BSA Policy is reviewed:" },
        {
          type: "ul",
          items: [
            "At least annually",
            "Following regulatory changes",
            "Following material business changes",
            "Following significant compliance incidents",
          ],
        },
      ],
    },
    {
      id: "policy-enforcement",
      number: "57",
      title: "Policy Enforcement",
      blocks: [
        { type: "p", text: "Failure to comply with this policy may result in:" },
        {
          type: "ul",
          items: [
            "Disciplinary action",
            "Suspension",
            "Termination",
            "Regulatory reporting",
            "Legal action",
          ],
        },
      ],
    },
    {
      id: "management-oversight",
      number: "58",
      title: "Management Oversight",
      blocks: [
        { type: "p", text: "Senior management is responsible for ensuring:" },
        {
          type: "ul",
          items: [
            "Adequate compliance resources",
            "Proper staffing",
            "Effective controls",
            "Compliance oversight",
            "Regulatory readiness",
          ],
        },
      ],
    },
    {
      id: "approval",
      number: "59",
      title: "Board & Executive Approval Statement",
      blocks: [
        {
          type: "p",
          text: "This AML/BSA Compliance Policy has been reviewed and approved by the management of Aixona LLC and applies to all applicable operations associated with CashTap.",
        },
      ],
    },
    {
      id: "conclusion",
      number: "60",
      title: "Conclusion",
      blocks: [
        {
          type: "p",
          text: "CashTap is committed to maintaining a comprehensive, risk-based AML/BSA compliance framework designed to comply with applicable laws and protect the integrity of the financial system.",
        },
        {
          type: "p",
          text: "This policy applies to all employees, contractors, vendors, users, merchants, and associated parties interacting with the CashTap platform.",
        },
      ],
    },
  ],
  footerNote: "Version 1.0 — January 2026. Initial AML/BSA Compliance Policy.",
};
